/**
 * RevokedIntegrationsBanner (TV1-2446 slice 7, design D4/D5)
 *
 * Org-wide, non-dismissible banner listing every currently-revoked integration (of any kind),
 * mounted directly above `<UserNavbar/>` in `AuthenticatedLayout` — visible to EVERY org member,
 * not just the person who can act on it. It disappears live the moment a row is restored, because
 * `revokedIntegrationsBannerPolicy`'s subscription feeds `entries` straight off the readmodel.
 *
 * Visual language mirrors `SuspensionBanner` (red-1/3/9 via `light-dark()`).
 *
 * `useCan( 'sales-channels:integrations:configure' )` decides the row's CTA: a member who can
 * configure integrations gets a Reconnect link straight to the channel detail page; everyone
 * else gets the "ask an administrator" copy instead. There is deliberately no kind-based
 * filtering here — the banner's whole point is org-wide visibility of ANY revoked integration.
 */

import { Anchor, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCan } from '@hooks/useCapability';
import { integrationsTestIds } from '@domains/integrations/testids';
import { useRevokedIntegrationsBannerSelector, selectors } from '../../state/revokedIntegrationsBanner.store';
import { channelReconnectPath } from '../../../channels/state/credentialRevocation';
import styles from './RevokedIntegrationsBanner.module.css';

export function RevokedIntegrationsBanner() {
  const { t } = useTranslation( 'channels' );
  const canReconnect = useCan( 'sales-channels:integrations:configure' );
  const revoked = useRevokedIntegrationsBannerSelector( selectors.selectRevokedIntegrations );

  if ( revoked.length === 0 ) return null;

  return (
    <Stack gap={0} className={styles.banner} data-testid={integrationsTestIds.revokedIntegrationsBanner}>
      {revoked.map(( entry ) => (
        <Stack
          key={entry.integrationId}
          gap={2}
          px="md"
          py="xs"
          className={styles.row}
          data-testid={integrationsTestIds.revokedIntegrationsBannerItem( entry.integrationId )}
        >
          <Text fz="sm">
            {t( 'revoked_integrations_banner_message', { label: entry.label })}
            {' '}
            {canReconnect ? (
              <Anchor
                component={Link}
                to={channelReconnectPath( entry.integrationId )}
                fz="sm"
                fw={600}
                className={styles.reconnect}
                data-testid={integrationsTestIds.revokedIntegrationsBannerReconnect( entry.integrationId )}
              >
                {t( 'revoked_integrations_banner_reconnect' )}
              </Anchor>
            ) : (
              <Text
                component="span"
                fz="sm"
                data-testid={integrationsTestIds.revokedIntegrationsBannerNoPermission( entry.integrationId )}
              >
                {t( 'revoked_integrations_banner_no_permission' )}
              </Text>
            )}
          </Text>
        </Stack>
      ))}
    </Stack>
  );
}

export default RevokedIntegrationsBanner;
